/* ==================================================================
   PRO MINT CARE — MOTION
   ------------------------------------------------------------------
   Scroll behaviours: rise, words, mask, draw, settle. Plus the
   before/after wipe.

   All of it fails open. The hidden state is armed per-element by this
   script and only on elements it is actively observing, so:

     - script never runs        -> nothing armed, page renders plainly
     - observer never reports   -> scroll fallback, then a hard timeout
     - section injected later   -> unarmed, therefore visible

   There is no global "hide everything" flag a later-rendered section
   could get stranded behind.

   Parallax is deliberately absent from this file: it is a scroll-driven
   CSS animation, so it runs on the compositor and costs nothing per
   frame. Never reintroduce it as a scroll listener.
   ================================================================== */

(function () {
  'use strict';

  var SELECTOR = '.pm-rise, .pm-words, .pm-mask, .pm-draw, .pm-settle';
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------
     Words — split a headline so each word can arrive in turn.

     Walks text nodes rather than rewriting innerHTML, so inline markup
     the merchant typed (<em>, <strong>, a link) survives intact. Spaces
     stay as real text nodes between the spans: without them the inline
     blocks lose their wrapping opportunities and a long headline
     overflows its column instead of breaking.
     ------------------------------------------------------------------ */

  function splitWords(el) {
    if (el.dataset.pmSplit) return;
    el.dataset.pmSplit = 'true';

    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var textNodes = [];
    var node;
    while ((node = walker.nextNode())) {
      if (node.nodeValue.trim()) textNodes.push(node);
    }

    var index = 0;

    textNodes.forEach(function (textNode) {
      var parts = textNode.nodeValue.split(/(\s+)/);
      var frag = document.createDocumentFragment();

      parts.forEach(function (part) {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          frag.appendChild(document.createTextNode(part));
          return;
        }
        var span = document.createElement('span');
        span.className = 'pm-word';
        span.style.setProperty('--pm-i', index++);
        span.textContent = part;
        frag.appendChild(span);
      });

      textNode.parentNode.replaceChild(frag, textNode);
    });
  }

  /* ------------------------------------------------------------------
     Reveal
     ------------------------------------------------------------------ */

  function initReveals(scope) {
    var items = [].slice.call(
      (scope || document).querySelectorAll(SELECTOR)
    ).filter(function (el) {
      return !el.classList.contains('is-armed');
    });

    if (!items.length) return;

    // Nothing to drive the reveal, or the visitor asked for stillness:
    // leave everything unarmed, which means visible.
    if (reduceMotion || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0 });

    items.forEach(function (el) {
      if (el.classList.contains('pm-words')) splitWords(el);
      el.classList.add('is-armed');
      observer.observe(el);
    });

    // Fallback 1: if the observer never reports, scrolling still reveals.
    function sweep() {
      var limit = window.innerHeight * 0.94;
      var pending = 0;
      items.forEach(function (el) {
        if (el.classList.contains('is-in')) return;
        if (el.getBoundingClientRect().top < limit) el.classList.add('is-in');
        else pending++;
      });
      if (!pending) stop();
    }

    function stop() {
      window.removeEventListener('scroll', sweep);
      window.removeEventListener('resize', sweep);
    }

    window.addEventListener('scroll', sweep, { passive: true });
    window.addEventListener('resize', sweep, { passive: true });
    sweep();

    // Fallback 2: nothing this script armed stays hidden past this.
    setTimeout(function () {
      items.forEach(function (el) { el.classList.add('is-in'); });
      observer.disconnect();
      stop();
    }, 2500);
  }

  /* ------------------------------------------------------------------
     Before / after wipe

     A real range input sits invisibly over the frame, so the control
     is draggable, keyboard-operable and announced correctly without
     any custom ARIA or pointer bookkeeping.
     ------------------------------------------------------------------ */

  function initCompare(scope) {
    [].slice.call(
      (scope || document).querySelectorAll('[data-pm-compare]:not([data-pm-ready])')
    ).forEach(function (el) {
      var range = el.querySelector('[data-pm-compare-range]');
      if (!range) return;

      el.setAttribute('data-pm-ready', '');
      el.style.setProperty('--pm-split', range.value + '%');

      // Dragging fires input far faster than the screen refreshes, so
      // coalesce writes to one per frame.
      var frame = null;
      range.addEventListener('input', function () {
        if (frame) return;
        frame = requestAnimationFrame(function () {
          frame = null;
          el.style.setProperty('--pm-split', range.value + '%');
        });
      });
    });
  }

  /* ------------------------------------------------------------------
     Marquee

     The strip is a native scroll container holding the same set of
     frames twice. Drift is applied by nudging scrollLeft, not by
     transforming a track, which means the visitor can swipe, wheel,
     drag the scrollbar, tab into it or press the arrows at any moment
     and the two never fight each other.

     It yields immediately to any of those and resumes once the visitor
     has stopped. Reduced motion switches the drift off entirely and
     leaves a perfectly ordinary scrollable strip.
     ------------------------------------------------------------------ */

  var supportsInert = 'inert' in HTMLElement.prototype;

  function initMarquee(scope) {
    [].slice.call(
      (scope || document).querySelectorAll('[data-pm-marquee]:not([data-pm-ready])')
    ).forEach(function (el) {
      el.setAttribute('data-pm-ready', '');

      // Where `inert` is unsupported the duplicate set would be
      // tabbable, so the same links would be traversed twice.
      if (!supportsInert) {
        [].slice.call(el.querySelectorAll('[aria-hidden="true"] a, [aria-hidden="true"] button'))
          .forEach(function (n) { n.setAttribute('tabindex', '-1'); });
      }

      var section = el.closest('.pm-band') || document;
      var prev = section.querySelector('[data-pm-marquee-prev]');
      var next = section.querySelector('[data-pm-marquee-next]');

      function step() {
        var item = el.querySelector('.pm-marquee__item');
        if (!item) return el.clientWidth * 0.8;
        var gap = parseFloat(getComputedStyle(el.querySelector('.pm-marquee__track')).columnGap) || 0;
        return item.getBoundingClientRect().width + gap;
      }

      // One set's width — the point at which the scroll position can be
      // wrapped without anything appearing to jump.
      function loopWidth() {
        var t = el.querySelector('.pm-marquee__track');
        return t ? t.getBoundingClientRect().width : 0;
      }

      // Forward-only. Wrapping at scrollLeft <= 0 as well would fire on
      // the very first scroll event, since the strip rests at 0, and
      // teleport the visitor into the duplicate set.
      function wrap() {
        var w = loopWidth();
        if (w > 0 && el.scrollLeft >= w) el.scrollLeft -= w;
      }

      function nudge(dir) {
        hold(900);
        var w = loopWidth();
        // Going back from the start would hit the browser's hard stop at
        // 0, so jump an identical set forward first — visually identical,
        // and the move back then has somewhere to go.
        if (dir < 0 && w > 0 && el.scrollLeft < step()) el.scrollLeft += w;
        el.scrollBy({ left: step() * dir, behavior: reduceMotion ? 'auto' : 'smooth' });
      }

      if (prev) prev.addEventListener('click', function () { nudge(-1); });
      if (next) next.addEventListener('click', function () { nudge(1); });

      el.addEventListener('scroll', wrap, { passive: true });

      if (reduceMotion || !el.hasAttribute('data-pm-marquee-auto')) return;

      // Seconds per frame -> pixels per second, so the Theme Editor
      // speed setting keeps meaning the same thing it used to.
      var secondsPerFrame = parseFloat(getComputedStyle(el).getPropertyValue('--pm-marquee-speed')) || 10;
      var paused = false;
      var resumeAt = 0;

      function hold(ms) { resumeAt = Date.now() + ms; }

      ['pointerenter', 'focusin'].forEach(function (ev) {
        el.addEventListener(ev, function () { paused = true; });
      });
      ['pointerleave', 'focusout'].forEach(function (ev) {
        el.addEventListener(ev, function () { paused = false; });
      });
      ['wheel', 'touchstart', 'pointerdown'].forEach(function (ev) {
        el.addEventListener(ev, function () { hold(2000); }, { passive: true });
      });
      document.addEventListener('visibilitychange', function () {
        paused = document.hidden;
      });

      var lastFrame = 0;
      function tick(now) {
        if (!lastFrame) lastFrame = now;
        var delta = Math.min(now - lastFrame, 50);
        lastFrame = now;

        if (!paused && Date.now() > resumeAt) {
          var pxPerSecond = step() / secondsPerFrame;
          el.scrollLeft += (pxPerSecond * delta) / 1000;
          wrap();
        }
        requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
    });
  }

  /* ------------------------------------------------------------------
     Product chooser

     The markup ships every recommendation as real, readable content.
     This turns that list into a two-step picker. If the script never
     runs the visitor still gets a usable list of products with reasons
     attached — a longer page, not a broken one.
     ------------------------------------------------------------------ */

  function initChooser(scope) {
    [].slice.call(
      (scope || document).querySelectorAll('[data-pm-chooser]:not([data-pm-ready])')
    ).forEach(function (root) {
      var results = root.querySelector('[data-pm-results]');
      if (!results) return;

      var cards = [].slice.call(results.querySelectorAll('.pm-chooser__result'));
      if (!cards.length) return;

      root.setAttribute('data-pm-ready', '');
      root.classList.add('is-interactive');

      var fallback = root.querySelector('[data-pm-fallback]');
      var reset = root.querySelector('[data-pm-reset]');
      var state = { type: null, problem: null };

      function render() {
        // Nothing chosen yet: show nothing rather than everything, so
        // the question reads as a question.
        var ready = state.type && state.problem;
        var shown = 0;

        cards.forEach(function (card) {
          var match = ready &&
            card.getAttribute('data-pm-for-type') === state.type &&
            card.getAttribute('data-pm-for-problem') === state.problem;
          card.hidden = !match;
          if (match) shown++;
        });

        if (fallback) fallback.hidden = !(ready && shown === 0);
        if (reset) reset.hidden = !(state.type || state.problem);
        results.classList.toggle('is-answered', !!ready);
      }

      function bind(attr, key) {
        [].slice.call(root.querySelectorAll('[' + attr + ']')).forEach(function (btn) {
          btn.setAttribute('aria-pressed', 'false');
          btn.addEventListener('click', function () {
            state[key] = btn.getAttribute(attr);
            [].slice.call(root.querySelectorAll('[' + attr + ']')).forEach(function (other) {
              var on = other === btn;
              other.classList.toggle('is-on', on);
              other.setAttribute('aria-pressed', on ? 'true' : 'false');
            });
            render();
          });
        });
      }

      bind('data-pm-type', 'type');
      bind('data-pm-problem', 'problem');

      if (reset) {
        reset.addEventListener('click', function () {
          state.type = null;
          state.problem = null;
          [].slice.call(root.querySelectorAll('.pm-chooser__opt')).forEach(function (b) {
            b.classList.remove('is-on');
            b.setAttribute('aria-pressed', 'false');
          });
          render();
        });
      }

      render();
    });
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */

  function boot(scope) {
    initReveals(scope);
    initCompare(scope);
    initMarquee(scope);
    initChooser(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
